/**
 * FillTracker - Spawn Area Tracking
 * 
 * Tracks the total area of spawned packages vs container capacity
 * to determine when to trigger the finishing phase.
 */

import { PhysicsBody } from '../engine/PhysicsWorld';

/**
 * Fill tracking state
 */
export interface FillState {
    /** Total area of all spawned packages */
    totalSpawnedArea: number;
    /** Maximum container area */
    containerArea: number;
    /** Current fill ratio (0-1) */
    fillRatio: number;
    /** Sort Left percentage (100% at start, 0% at threshold) */
    sortLeftPercent: number;
    /** Whether fill threshold has been reached */
    thresholdReached: boolean;
}

/**
 * FillTracker class
 */
export class FillTracker {
    private totalSpawnedArea = 0;
    private containerArea = 0;
    private fillThreshold = 0.7;  // Default, will be set from difficulty

    /**
     * Set the container area (call when container is created)
     */
    setContainerArea(area: number): void {
        this.containerArea = area;
    }

    /**
     * Set the fill threshold from difficulty settings
     */
    setFillThreshold(threshold: number): void {
        this.fillThreshold = threshold;
    }

    /**
     * Reset tracking (for new game)
     */
    reset(): void {
        this.totalSpawnedArea = 0;
    }

    /**
     * Track a spawned package.
     * Call this whenever a new package is created.
     */
    trackSpawn(body: PhysicsBody): void {
        const area = this.calculateBodyArea(body);
        this.totalSpawnedArea += area;
    }

    /**
     * Calculate the area of a physics body
     */
    private calculateBodyArea(body: PhysicsBody): number {
        const { userData } = body;

        switch (userData.shapeType) {
            case 'box':
            case 'bag':
                return (userData.width ?? 50) * (userData.height ?? 50);
            
            case 'triangle': {
                // Equilateral triangle area: (3 * sqrt(3) / 4) * r^2
                const r = userData.radius ?? 30;
                return (3 * Math.sqrt(3) / 4) * r * r;
            }
            
            default:
                return 0;
        }
    }

    /**
     * Get current fill ratio (0-1)
     */
    getFillRatio(): number {
        if (this.containerArea <= 0) return 0;
        return this.totalSpawnedArea / this.containerArea;
    }

    /**
     * Get "Sort Left" percentage.
     * 100% at start, counts down to 0% when threshold is reached.
     */
    getSortLeftPercent(): number {
        if (this.fillThreshold <= 0) return 100;
        
        const ratio = this.getFillRatio();
        const percent = 100 - (ratio / this.fillThreshold * 100);
        return Math.max(0, Math.round(percent));
    }

    /**
     * Check if fill threshold has been reached
     */
    hasReachedThreshold(): boolean {
        return this.getFillRatio() >= this.fillThreshold;
    }

    /**
     * Get complete fill state
     */
    getState(): FillState {
        const fillRatio = this.getFillRatio();
        
        return {
            totalSpawnedArea: this.totalSpawnedArea,
            containerArea: this.containerArea,
            fillRatio,
            sortLeftPercent: this.getSortLeftPercent(),
            thresholdReached: fillRatio >= this.fillThreshold,
        };
    }

    /**
     * Get total spawned area
     */
    getTotalSpawnedArea(): number {
        return this.totalSpawnedArea;
    }
}
